import {
    ConditionalTriggerConfig,
    FileWatchTriggerConfig,
    IHttpTrigger, IScheduleParameters,
    ManualTriggerConfig,
    MonthlyCycleType, ScheduleCycleType, Triggers
} from "./ITriggerData";
import { State } from "./State";

import { getTimeZoneSelectModel } from "../components/Triggers/TimeZoneSelectModelSelector";
import Time from "shared/Time";
import { HttpAuthorizationType } from "processEditor/components/Triggers/HttpTrigger";
import { QueueTriggerConfig } from "processEditor/components/Triggers/QueueTrigger";
import { ServiceBusTriggerConfig } from "processEditor/components/Triggers/ServiceBusTrigger";
import { RabbitMQTriggerConfig } from "processEditor/components/Triggers/RabbitMQTrigger";
import { DataMode, Encoding, TcpTriggerConfig, TerminationEvent } from "processEditor/components/Triggers/TcpTrigger";

export function getDefaultTriggerConfiguration(type: string, state: State): undefined | ConditionalTriggerConfig | FileWatchTriggerConfig
    | IHttpTrigger | IScheduleParameters | ManualTriggerConfig | QueueTriggerConfig | RabbitMQTriggerConfig | ServiceBusTriggerConfig | TcpTriggerConfig {
    switch (type) {
        case Triggers.ConditionalTrigger.triggerType:
            return defaultConditionalTriggerConfig;

        case Triggers.FileWatchTrigger.triggerType:
            return defaultFileWatchTriggerConfig;

        case Triggers.HttpTrigger.triggerType:
        case Triggers.HttpApiTrigger.triggerType: {
            return defaultHttpTriggerConfig;
        }

        case Triggers.ManualTrigger.triggerType:
            return {};

        case Triggers.QueueTrigger.triggerType:
            return {};

        case Triggers.ServiceBusTrigger.triggerType:
            return {
                queueName: "",
                connectionString: "",
                maxConcurrentMessages: 10,
                messagePrefetchCount: 1, // Actually batch of messages but did not rename property for backwards compatibility reasons. 
                consumeMessageImmediately: true,
                reply: false,
                replyErrors: false,
                replyTo: "",
                retryMessageProcessingIfExceptionThrown: false
            };

        case Triggers.RabbitMQTrigger.triggerType:
            return {
                maxConcurrentMessages: 1
            };

        case Triggers.ScheduleTrigger.triggerType:
            return getDefaultScheduleTriggerConfig(state);

        case Triggers.TcpTrigger.triggerType: {
            return {
                dataMode: DataMode.Text,
                encoding: Encoding.ASCII,
                idleTimeoutSeconds: 60*60,
                listenPort: 38000,
                listenAddress: "0.0.0.0",
                maxConnections: 1,
                terminationEvent: TerminationEvent.CharacterSequence,
                terminationHexString: "0a", //<LF>
                allowedIps: "0.0.0.0/0",
            } as TcpTriggerConfig
        }
    }
}

function getDefaultScheduleTriggerConfig(state: State): IScheduleParameters {
    const timezoneDetails = getTimeZoneSelectModel(state);
    return {
        startTimeMinute: "00",
        startTimeHour: "00",
        endTimeMinute: "00",
        endTimeHour: "00",
        endTime: "00:00",
        startTime: "00:00",
        recurring: false,
        repeatDelay: 0,
        repeatDelaySeconds: 0,
        limitToOneConcurrentExecution: true,
        cycleRecurEvery: 1,
        cycleType: ScheduleCycleType.Daily,
        cycleLength: 1,
        seasonStartDate: Time.toLocalDate(new Date().toISOString()),
        seasonEndDate: null,
        cycleDaysOfWeek: "None",
        cycleMonths: "None",
        cycleDaysString: "",
        cycleDayRanks: "None",
        monthlyCycleType: MonthlyCycleType.OnDays,
        repeatDelayType: 1,
        timeZone: timezoneDetails?.currentTimeZone
    };
}

const defaultConditionalTriggerConfig: ConditionalTriggerConfig = {
    pollingInterval: 60,
    processGuid: "",
    limitToOneConcurrentExecution: false
};

const defaultFileWatchTriggerConfig: FileWatchTriggerConfig = {
    version: 2,
    dirToWatch: "",
    fileMask: "*.xml",
    includeSubDirectories: false,
    maxFilesPerBatch: 1000,
    pollIntervalSeconds: 10
};

const defaultHttpTriggerConfig: IHttpTrigger = {
    routeTemplate: "",
    allowedSchemes: "HTTPS",
    auth: HttpAuthorizationType.ApiPolicy,
    corsEnabled: false,
    allowedOrigins: "",
    httpMethod: "ANY",
    isPrivate: false
};
